import React, { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { AxiosError } from "axios";

import {
  fetchDataRequest,
  fetchLicensesRequest,
  paginationUpdate,
  resetFilter,
  selectAvailableLicense,
  selectCustomerPrincipalList,
  selectFilter,
  selectIsFetching,
  selectLicense,
  selectPage,
  selectPages,
  selectSort,
  selectTotalLicense,
  setCustomerId,
  updateFilter,
  updateFilterSearch,
  updateSort,
} from "../../store/manageUsers";
import Message from "../../components/Table/components/Message";
import License from "./components/License";
import ManageUsersTable from "./components/ManageUsersTable";
import { TableHeaderSort } from "../../components/Table/components/TableHeader/types";
import FilterSection from "./components/FilterSection";
import ButtonPrimary from "../../components/ButtonPrimary";
import { Value } from "../../components/SearchInput";
import UserModal from "./components/UserModal";
import userAPIService from "../../services/api/user";
import Toaster from "../../services/toaster";
import {
  ICustomerPrincipal,
  IPrincipalCreatePayload,
  IPrincipalLicence,
} from "../../services/api/user/types";
import Pagination from "../../components/Pagination";
import { subscriptions } from "../../config/router/routes";
import { selectPrincipalData } from "../../store/user";
import { selectCurrentCustomerSubscription } from "../../store/subscriptions";
import { selectBrandCode, selectOriginSettings } from "../../store/metadata";
import { useAbility, useDialog } from "../../hooks";
import { ACTION, SUBJECT } from "../../constants/permissions";
import { getExistErrorKey } from "../../utils/common/errors";
import { checkIsETagError } from "../../utils/common";
import { IErrorDetail } from "../../utils/common/types";
import { RESPONSE_ERROR_PROBLEM } from "../../utils/common/contants";
import authPasswordAPIService from "../../services/api/auth";
import { ContentLoaderContainer } from "../../components/Loader/ContentLoader/ContentLoaderContainer";
import { ITableDataParams } from "../../components/Table/types";
import { ConfirmModal } from "../../components/ConfirmModal/ConfirmModal";

import "./styles.scss";

export const ManageUsers: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [showAddLicenceModal, setShowAddLicenceModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ICustomerPrincipal>();
  const [selectedUserEtag, setSelectedUserEtag] = useState<string>();
  const [isUserHaveLicense, setIsUserHaveLicense] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>();
  const [isFetchingResetPassword, setIsFetchingResetPassword] = useState(false);
  const [isUpdateFetching, setIsUpdateFetching] = useState(false);
  const isFetching = useSelector(selectIsFetching);
  const principalList = useSelector(selectCustomerPrincipalList);
  const license = useSelector(selectLicense);
  const totalLicense = useSelector(selectTotalLicense);
  const availableLicense = useSelector(selectAvailableLicense);
  const currentPage = useSelector(selectPage);
  const pages = useSelector(selectPages);
  const filter = useSelector(selectFilter);
  const userData = useSelector(selectPrincipalData);
  const sort = useSelector(selectSort);
  const currentSubscription = useSelector(selectCurrentCustomerSubscription);
  const originSettings = useSelector(selectOriginSettings);
  const isEmailAvailable =
    originSettings?.principal_email_synchronized_from_username === false;
  const isUsernameDisabled =
    originSettings?.principal_username_is_immutable === true;
  const brand = useSelector(selectBrandCode);
  const ability = useAbility();

  const isCanEdit = useMemo(() => {
    return userData?.id !== selectedUser?.id;
  }, [userData, selectedUser]);

  const [
    isShowResetPasswordDialog,
    openResetPasswordDialog,
    closeResetPasswordDialog,
  ] = useDialog();

  useEffect(() => {
    if (userData?.customer_id) {
      dispatch(setCustomerId(userData?.customer_id));
      dispatch(fetchDataRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.customer_id]);

  useEffect(() => {
    if (currentSubscription?.id) {
      dispatch(fetchLicensesRequest(currentSubscription.id));
    }
  }, [currentSubscription, dispatch]);

  const handleResetFilter = () => {
    dispatch(resetFilter());
  };

  const handleRoleChange = (value: string) => {
    dispatch(updateFilter({ role: value }));
  };

  const handleSearch = (value: Value) => {
    dispatch(updateFilterSearch(value));
  };

  const handleSearchSubmit = (value: Value) => {
    dispatch(updateFilterSearch(value));
  };

  const handleOpenModal = () => {
    setIsUserModalOpen(true);
  };

  const handleCloseModal = () => {
    setErrors({});
    setIsUserModalOpen(false);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      paginationUpdate({ pageSize: Number(event.target.value), page: 0 }),
    );
  };

  const handlePageChange = (page: number) => {
    dispatch(paginationUpdate({ page }));
  };

  const handleSort = (sortData: TableHeaderSort) => {
    dispatch(updateSort(sortData));
  };

  const onEditUser = async (
    user: ITableDataParams<ICustomerPrincipal>["rowData"],
    haveLicense: boolean,
  ) => {
    if (user) {
      try {
        const currentUserData = await userAPIService.getCustomerPrincipal(
          user.customer_id,
          user.id,
        );

        setSelectedUser(currentUserData.data);
        setSelectedUserEtag(currentUserData.eTag);
        setIsUserHaveLicense(haveLicense);
        setIsEditUserModalOpen(true);
      } catch (e) {
        Toaster.error({
          content: t("somethingWentWrong"),
        });
      }
    }
  };

  const handleEditCloseModal = () => {
    setErrors({});
    setIsEditUserModalOpen(false);
    setSelectedUser(undefined);
  };

  const handleShowAddLicenceModal = () => {
    setShowAddLicenceModal(true);
  };

  const handleCloseAddLicenceModal = () => {
    setShowAddLicenceModal(false);
  };

  const handleAddLicenceModal = () => {
    history.push(subscriptions.path, { showManageLicensesDialog: true });
  };

  const handleCreateUser = async (
    data: IPrincipalCreatePayload,
    active: boolean,
  ) => {
    setErrors({});
    if (userData?.customer_id) {
      try {
        setIsUpdateFetching(true);
        const createdUser = await userAPIService.createCustomerPrincipal(
          userData?.customer_id,
          data,
        );
        dispatch(fetchDataRequest());
        handleCloseModal();
        setErrors({});
        if (active) {
          updatePrincipalLicense(createdUser, active);
        }
      } catch (error) {
        onCreateError(error);
      } finally {
        setIsUpdateFetching(false);
      }
    }
  };

  const handleEditUser = async (
    data: IPrincipalCreatePayload,
    active: boolean,
  ) => {
    setErrors({});

    if (userData?.customer_id && selectedUser?.id) {
      try {
        setIsUpdateFetching(true);
        /* eslint-disable @typescript-eslint/camelcase */
        if (isCanEdit) {
          await userAPIService.editCustomerPrincipal(
            userData?.customer_id,
            selectedUser.id,
            {
              name: data.name,
              email: data.email,
              username: data.username,
              customer_role: ability.can(ACTION.WRITE, SUBJECT.ADMIN)
                ? data.customer_role
                : undefined,
            },
            selectedUserEtag,
          );
        }
        if (active !== isUserHaveLicense) {
          await updatePrincipalLicense(selectedUser, active);
        }
        dispatch(fetchDataRequest());
        handleEditCloseModal();
        setErrors({});
      } catch (error) {
        onUpdateError(error);
      } finally {
        setIsUpdateFetching(false);
      }
    }
  };

  const updatePrincipalLicense = async (
    principal: ICustomerPrincipal,
    isAddLicence: boolean,
  ) => {
    if (!availableLicense && isAddLicence) {
      handleShowAddLicenceModal();
      return;
    }

    if (userData?.customer_id && license && currentSubscription?.id) {
      /* eslint-disable @typescript-eslint/camelcase */
      const newLicense = isAddLicence
        ? [
            ...license.licenses,
            {
              principal_id: principal.id,
              pending_revocation: false,
            },
          ]
        : license.licenses.filter(({ principal_id }: IPrincipalLicence) => {
            return principal.id !== principal_id;
          });
      /* eslint-enable @typescript-eslint/camelcase */

      try {
        setIsUpdateFetching(true);
        await userAPIService.editLicense(
          {
            customerId: userData?.customer_id,
            subscriptionId: currentSubscription.id,
          },
          newLicense,
          license.eTag,
        );
        dispatch(fetchLicensesRequest(currentSubscription.id));
      } catch (error) {
        if (checkIsETagError(error.response.data.error_code)) {
          Toaster.error({
            content: t(`error.${error.response.data.error_code}`),
          });
          return;
        }

        Toaster.error({
          content: t("somethingWentWrong"),
        });
      } finally {
        setIsUpdateFetching(false);
      }
    }
  };

  const onCreateError = (error: AxiosError) => {
    const errorDetails = error.response?.data?.details;
    const receivedErrors: Record<string, string> = {};
    let isHandledError = false;

    errorDetails?.forEach((errorDetail: IErrorDetail) => {
      if (error.response?.status === 409) {
        if (errorDetail.problem === RESPONSE_ERROR_PROBLEM.OwnerAssigned) {
          receivedErrors[errorDetail.path] = t(`error.${errorDetail.problem}`);
          isHandledError = true;
        } else {
          receivedErrors[errorDetail.path] = t(getExistErrorKey(errorDetail));
          isHandledError = true;
        }
      }

      if (error.response?.status === 422) {
        if (
          errorDetail.path === "email" &&
          errorDetail.problem === RESPONSE_ERROR_PROBLEM.OutOfRange
        ) {
          receivedErrors.email = t(`error.InvalidEmailFormat`);
          isHandledError = true;
        }

        if (
          errorDetail.path === "email" &&
          errorDetail.problem === RESPONSE_ERROR_PROBLEM.Illegal
        ) {
          receivedErrors.email = t(`error.latinCharactersOnly`);
          isHandledError = true;
        }

        if (
          errorDetail.path === "email" &&
          errorDetail.problem === RESPONSE_ERROR_PROBLEM.InvalidValue
        ) {
          receivedErrors.email = t(`error.InvalidEmailFormat`);
          isHandledError = true;
        }

        if (
          errorDetail.path === "name" &&
          errorDetail.problem === RESPONSE_ERROR_PROBLEM.OutOfRange
        ) {
          receivedErrors.name = t(`error.NameIsTooLong`);
          isHandledError = true;
        }
      }
    });

    setErrors((state) => {
      return { ...state, ...receivedErrors };
    });

    if (isHandledError) {
      return;
    }

    Toaster.error({
      content: t("somethingWentWrong"),
    });
  };

  const onUpdateError = (error: any) => {
    const errorDetails = error.response?.data?.details;
    const receivedErrors: Record<string, string> = {};
    let isHandledError = false;

    errorDetails?.forEach((errorDetail: IErrorDetail) => {
      if (error.response?.status === 409 && brand) {
        receivedErrors[errorDetail.path] = t(getExistErrorKey(errorDetail));
        isHandledError = true;
        setErrors(receivedErrors);
        return;
      }

      if (error.response?.status === 422) {
        if (
          errorDetail.path === "email" &&
          (errorDetail.problem === RESPONSE_ERROR_PROBLEM.InvalidValue ||
            errorDetail.problem === RESPONSE_ERROR_PROBLEM.OutOfRange)
        ) {
          receivedErrors.email = t(`error.InvalidEmailFormat`);
          isHandledError = true;
        }

        if (
          errorDetail.path === "email" &&
          errorDetail.problem === RESPONSE_ERROR_PROBLEM.Illegal
        ) {
          receivedErrors.email = t(`error.latinCharactersOnly`);
          isHandledError = true;
        }

        if (
          errorDetail.path === "name" &&
          errorDetail.problem === RESPONSE_ERROR_PROBLEM.OutOfRange
        ) {
          receivedErrors.name = t(`error.NameIsTooLong`);
          isHandledError = true;
        }
      }
    });

    setErrors((state) => {
      return { ...state, ...receivedErrors };
    });

    if (isHandledError) {
      return;
    }

    Toaster.error({
      content: t("somethingWentWrong"),
    });
  };

  const isUserCanResetPassword = (): boolean => {
    return (
      isCanEdit &&
      selectedUser?.customer_role !== "Owner" &&
      (ability.can(ACTION.WRITE, SUBJECT.ADMIN) ||
        ability.can(ACTION.WRITE, SUBJECT.OWNER) ||
        ability.can(ACTION.READ, SUBJECT.TECHNICAL))
    );
  };

  const handleResetPassword = async () => {
    if (!selectedUser) {
      return;
    }
    try {
      setIsFetchingResetPassword(true);
      await authPasswordAPIService.resetPassword({
        username: selectedUser.username,
        // eslint-disable-next-line @typescript-eslint/camelcase
        tenant_code: selectedUser.tenant_code,
      });
      Toaster.success({ content: t("resetPasswordEmailSent") });
    } catch (error) {
      Toaster.error({ content: t("resetPasswordEmailFailed") });
    } finally {
      setIsFetchingResetPassword(false);
      closeResetPasswordDialog();
    }
  };

  const addLicencePermission = ability.can(ACTION.WRITE, SUBJECT.BILLING);

  return (
    <section className="ams-manage-users">
      {isFetching ? (
        <ContentLoaderContainer />
      ) : (
        <>
          <h1 className="ams-manage-users__header">
            {t("manageUsers")}
            <License
              availableLicense={availableLicense}
              totalLicense={totalLicense}
            />
          </h1>

          <div className="ams-manage-users__subheader mb-m">
            <FilterSection
              filterData={filter}
              handleResetFilter={handleResetFilter}
              handleRoleChange={handleRoleChange}
              handleSearch={handleSearch}
              handleSearchSubmit={handleSearchSubmit}
            />
            <ButtonPrimary className="ml-xs mt-m" onClick={handleOpenModal}>
              {t("addUser")}
            </ButtonPrimary>
          </div>

          {!principalList.length ? (
            <Message>{t("nothingToDisplay")}</Message>
          ) : (
            <ManageUsersTable
              data={principalList}
              defaultSort={sort}
              onEdit={onEditUser}
              onSort={handleSort}
            />
          )}
          <Pagination
            page={currentPage}
            pages={pages}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={handlePageChange}
          />

          {isUserModalOpen && brand && (
            <UserModal
              isShowMessage
              brand={brand}
              isSyncEmail={!isEmailAvailable}
              isOpen={isUserModalOpen}
              onSubmit={handleCreateUser}
              titleText={t("addUser")}
              confirmButtonText={t("inviteUser")}
              isLoading={isUpdateFetching}
              onDismiss={handleCloseModal}
              errors={errors}
            />
          )}

          {isEditUserModalOpen && brand && (
            <UserModal
              brand={brand}
              isUserNameDisabled={isUsernameDisabled}
              isSyncEmail={!isEmailAvailable}
              isCanEdit={isCanEdit}
              isOpen={isEditUserModalOpen}
              user={selectedUser}
              haveLicense={isUserHaveLicense}
              titleText={t("editUser")}
              confirmButtonText={t("update")}
              isLoading={isUpdateFetching}
              onDismiss={handleEditCloseModal}
              onSubmit={handleEditUser}
              onResetPassword={openResetPasswordDialog}
              isUserCanResetPassword={isUserCanResetPassword()}
              errors={errors}
            />
          )}
          {showAddLicenceModal && (
            <ConfirmModal
              title={t("additionalLicenseNeeded")}
              message={t("usingAllOfYouAvailableLicenses")}
              cancelBtnText={
                addLicencePermission ? t("addLicensesLater") : t("ok")
              }
              submitBtnText={addLicencePermission ? t("addLicensesNow") : ""}
              isOpen={showAddLicenceModal}
              onSubmit={handleAddLicenceModal}
              onDismiss={handleCloseAddLicenceModal}
            />
          )}

          {selectedUser && isShowResetPasswordDialog && isUserCanResetPassword && (
            <ConfirmModal
              title={t("resetPassword")}
              message={t("resetPasswordConfirmation", {
                email: selectedUser.email,
              })}
              submitBtnText={t("sendPasswordResetEmail")}
              isLoading={isFetchingResetPassword}
              isOpen={isShowResetPasswordDialog}
              onDismiss={closeResetPasswordDialog}
              onSubmit={handleResetPassword}
              className={"ams-reset-password-modal"}
            />
          )}
        </>
      )}
    </section>
  );
};
